import React, { useMemo } from "react";
import Drawer from "@mui/material/Drawer";
import { Box, AppBar, CardHeader } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  useCustomCover,
  EDITOR_ELEMENTS,
  EDITOR_ELEMENT_LABEL_KEYS,
  type EditorElement,
} from "../index";
import { FormBuilder } from "@/shared/components/Form";
import {
  makeCustomCoverTitleSchema,
  makeCustomCoverSubTitleSchema,
  makeCustomCoverButtonsSchema,
  makeCustomCoverBackgroundSchema,
  makeCustomCoverAlignSchema,
} from "../model/schemas";

const DrawerContainer = styled(Box)(() => ({
  width: 300,
}));

export const EditDrawer: React.FC = () => {
  const { t } = useTranslation();
  const {
    drawerOpen,
    selectedItem,
    title,
    subTitle,
    buttons,
    buttonsColor,
    buttonsSize,
    align,
    alignMobile,
    verticalAlign,
    verticalAlignMobile,
    backgrounds,
    brightness,
    onCloseDrawer,
    onChangeTitle,
    onChangeSubTitle,
    onChangeButtons,
    onChangeButtonsColor,
    onChangeButtonsSize,
    onChangeAlign,
    onChangeAlignMobile,
    onChangeVerticalAlign,
    onChangeVerticalAlignMobile,
    onChangeBackground,
    onChangeBrightness,
  } = useCustomCover();

  const schema = useMemo(() => {
    switch (selectedItem) {
      case EDITOR_ELEMENTS.title:
        return makeCustomCoverTitleSchema(t);
      case EDITOR_ELEMENTS.subTitle:
        return makeCustomCoverSubTitleSchema(t);
      case EDITOR_ELEMENTS.buttons:
        return makeCustomCoverButtonsSchema(t);
      case EDITOR_ELEMENTS.image:
        return makeCustomCoverBackgroundSchema(t);
      case EDITOR_ELEMENTS.align:
        return makeCustomCoverAlignSchema(t);
      default:
        return {};
    }
  }, [selectedItem, t]);

  const initialValues = useMemo(() => {
    switch (selectedItem) {
      case EDITOR_ELEMENTS.title:
        return { title };
      case EDITOR_ELEMENTS.subTitle:
        return { subTitle };
      case EDITOR_ELEMENTS.buttons: {
        // React Final Form требует массив для числовых индексов в точечной нотации
        const result: any = {
          buttonsColor,
          buttonsSize,
        };

        // Создаем массив для кнопок - React Final Form использует массив для числовых индексов
        result.buttons = [
          {
            text: buttons[0]?.text || "",
            link: buttons[0]?.link || "",
            variant: buttons[0]?.variant || "contained",
            disabled:
              buttons[0]?.disabled === undefined ? true : !buttons[0].disabled, // Switch показывает true когда кнопка включена
          },
          {
            text: buttons[1]?.text || "",
            link: buttons[1]?.link || "",
            variant: buttons[1]?.variant || "contained",
            disabled:
              buttons[1]?.disabled === undefined ? true : !buttons[1].disabled,
          },
        ];

        return result;
      }
      case EDITOR_ELEMENTS.image:
        return { background: backgrounds, brightness };
      case EDITOR_ELEMENTS.align:
        return { align, alignMobile, verticalAlign, verticalAlignMobile };
      default:
        return {};
    }
  }, [
    selectedItem,
    title,
    subTitle,
    buttons,
    buttonsColor,
    buttonsSize,
    backgrounds,
    brightness,
    align,
    alignMobile,
    verticalAlign,
    verticalAlignMobile,
  ]);

  const getTitle = () => {
    const labelKey = selectedItem
      ? EDITOR_ELEMENT_LABEL_KEYS[selectedItem as EditorElement]
      : undefined;

    return labelKey ? t(labelKey) : "";
  };

  const applyValues = (values: Record<string, any>) => {
    switch (selectedItem) {
      case EDITOR_ELEMENTS.title:
        onChangeTitle(values.title || {});
        break;
      case EDITOR_ELEMENTS.subTitle:
        onChangeSubTitle(values.subTitle || {});
        break;
      case EDITOR_ELEMENTS.buttons: {
        // React Final Form возвращает значения во вложенной структуре (buttons[0].text)
        const buttonsArray = values.buttons;

        // Обрабатываем первую кнопку - применяем изменения если массив существует
        if (
          buttonsArray &&
          Array.isArray(buttonsArray) &&
          buttonsArray.length > 0 &&
          buttonsArray[0]
        ) {
          const button0 = buttonsArray[0];
          onChangeButtons({
            button: 0,
            values: {
              text: button0.text,
              link: button0.link,
              variant: button0.variant,
              disabled: !button0.disabled, // Switch показывает true когда кнопка включена
            },
          });
        }

        // Обрабатываем вторую кнопку
        if (
          buttonsArray &&
          Array.isArray(buttonsArray) &&
          buttonsArray.length > 1 &&
          buttonsArray[1]
        ) {
          const button1 = buttonsArray[1];
          onChangeButtons({
            button: 1,
            values: {
              text: button1.text,
              link: button1.link,
              variant: button1.variant,
              disabled: !button1.disabled, // Switch показывает true когда кнопка включена
            },
          });
        }

        if (values.buttonsColor !== undefined) {
          onChangeButtonsColor(values.buttonsColor);
        }
        if (values.buttonsSize !== undefined) {
          onChangeButtonsSize(values.buttonsSize);
        }
        break;
      }
      case EDITOR_ELEMENTS.image:
        if (values.background) {
          Object.keys(values.background).forEach((key) => {
            const fileId = values.background[key];
            // Обрабатываем как null, так и пустую строку
            onChangeBackground({
              variant: key as any,
              fileId: fileId || null,
            });
          });
        }
        if (values.brightness !== undefined) {
          onChangeBrightness(values.brightness);
        }
        break;
      case EDITOR_ELEMENTS.align:
        if (values.align !== undefined) {
          onChangeAlign(values.align);
        }
        if (values.alignMobile !== undefined) {
          onChangeAlignMobile(values.alignMobile);
        }
        if (values.verticalAlign !== undefined) {
          onChangeVerticalAlign(values.verticalAlign);
        }
        if (values.verticalAlignMobile !== undefined) {
          onChangeVerticalAlignMobile(values.verticalAlignMobile);
        }
        break;
    }
  };

  const handleChange = (params: { values: Record<string, any> }) => {
    applyValues(params.values);
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen && !!selectedItem}
      onClose={onCloseDrawer}
      BackdropProps={{ invisible: true }}
      PaperProps={{
        sx: {
          backgroundColor: "background.default",
        },
      }}
      style={{ zIndex: 1399 }}
    >
      <DrawerContainer>
        <AppBar position="static" color="default" elevation={0}>
          <CardHeader title={getTitle()} />
        </AppBar>
        <Box sx={{ p: 2 }}>
          <FormBuilder
            key={selectedItem}
            formId={`custom-cover-edit-${selectedItem}`}
            schema={schema}
            initialValues={initialValues}
            onChange={handleChange}
            submitButton={false}
            resetButton={false}
            spacing={2}
            warnOnUnsavedChanges={false}
          />
        </Box>
      </DrawerContainer>
    </Drawer>
  );
};
